import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { RankingFullLeaderboardRow } from './RankingFullLeaderboardRow/index.tsx';
import type {
  FullLeaderboardPaginatedRankings_so5Ranking,
  FullRankingsQuery,
  FullRankingsQueryVariables,
} from './__generated__/index.graphql.ts';

const PAGINATED_RANKINGS_QUERY = gql`
  query FullRankingsQuery(
    $slug: String!
    $page: Int
    $pageSize: Int!
    $onlyFollowed: Boolean
  ) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        so5RankingsPaginated(
          page: $page
          pageSize: $pageSize
          onlyFollowed: $onlyFollowed
        ) {
          pages
          currentPage
          nodes {
            id
            score
            so5Lineup {
              id
              so5LeaderboardContender {
                slug
              }
            }
            ...RankingFullLeaderboardRow_so5Ranking
          }
        }
      }
    }
  }
  ${RankingFullLeaderboardRow.fragments.so5Ranking}
` as TypedDocumentNode<FullRankingsQuery, FullRankingsQueryVariables>;

const PAGE_SIZE = 5;

type Props = {
  slug: string;
  so5Lineup?: { id: string };
  onClickRanking?: (rankingId: string) => void;
  showAppearances?: boolean;
};

export const FullLeaderboard = ({
  slug,
  so5Lineup,
  onClickRanking,
  showAppearances,
}: Props) => {
  const { data, loading, loadMore } = usePaginatedQuery(
    PAGINATED_RANKINGS_QUERY,
    {
      variables: {
        page: 0,
        slug,
        pageSize: PAGE_SIZE,
      },
      connection: 'So5RankingsPaginated',
    }
  );

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        page:
          (data?.so5.so5Leaderboard.so5RankingsPaginated.currentPage || 0) + 1,
      });
    },
    !(
      (data?.so5.so5Leaderboard.so5RankingsPaginated.currentPage || 0) + 1 ===
      (data?.so5.so5Leaderboard.so5RankingsPaginated.pages || 0)
    ),
    loading
  );

  const { so5RankingsPaginated } = data?.so5.so5Leaderboard || {};
  const { nodes: so5Rankings } = so5RankingsPaginated || {};

  const myRanking = so5Rankings?.find(
    ranking => ranking.so5Lineup.id === so5Lineup?.id
  );

  return (
    <Vertical>
      <Vertical gap={3}>
        {loading ? (
          <LoadingIndicator fullHeight />
        ) : (
          so5Rankings?.map(ranking => {
            const scoreDiff = myRanking ? myRanking?.score - ranking.score : 0;
            return (
              <RankingFullLeaderboardRow
                key={ranking.id}
                so5Ranking={ranking}
                isMyRanking={ranking.so5Lineup.id === so5Lineup?.id}
                displayLineup={showAppearances}
                scoreDiff={scoreDiff}
                onClick={
                  onClickRanking
                    ? () =>
                        onClickRanking?.(
                          ranking.so5Lineup.so5LeaderboardContender.slug
                        )
                    : undefined
                }
              />
            );
          })
        )}
      </Vertical>
      <InfiniteScrollLoader />
    </Vertical>
  );
};

FullLeaderboard.fragments = {
  so5Ranking: gql`
    fragment FullLeaderboardPaginatedRankings_so5Ranking on So5Ranking {
      id
      score
    }
  ` as TypedDocumentNode<FullLeaderboardPaginatedRankings_so5Ranking>,
};
